import { Button, useNiceModal } from '@castlery/fortress';
import {
  checkoutRegistration,
  selectOrder,
  selectOrderLoading,
  getOrderByOrderNumber,
  selectCouponProcessing,
} from '@castlery/modules-order-domain';
import {
  enterApp,
  getAddressesByUserId,
  selectedCustomerId,
  useGetAddressesByUserIdQuery,
} from '@castlery/modules-user-domain';
import { useAppDispatch, useAppSelector } from '@castlery/shared-redux-store';
import { selectCheckoutBtnStatus } from '@castlery/modules-order-services';
import { useAsyncFn } from 'react-use';
import { useRouter } from 'next/navigation';
import { posRoutes } from '@castlery/config';
import { selectCheckoutAddress } from '@castlery/modules-composite-services';
import { changeAddressByOrderNumber } from '@castlery/modules-checkout-domain';
import { trackOfflineCheckout } from '@castlery/modules-tracking-services';

export const PosCheckoutButton = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [modal, modalContextHolder] = useNiceModal();

  const order = useAppSelector(selectOrder);
  const checkoutBtnStatus = useAppSelector(selectCheckoutBtnStatus);
  const orderLoading = useAppSelector(selectOrderLoading);
  const couponProcessing = useAppSelector(selectCouponProcessing);
  const customerId = useAppSelector(selectedCustomerId);
  useGetAddressesByUserIdQuery(customerId as number, {
    skip: !customerId,
  });
  const { billingAddress, shippingAddress } = useAppSelector(selectCheckoutAddress);

  const checkBlockedItems = (items: any[] | undefined) => {
    return (
      Array.isArray(items) &&
      items?.some((item) => item.is_region_outdated || item.is_price_outdated || item.stock_state === 'OUT_OF_STOCK')
    );
  };
  const showModal = () => {
    modal.warning({
      title: 'Oops!',
      subDesc: 'Some items here cannot be checked out, please check your cart！',
      showCancelBtn: false,
      confirmText: 'Got it',
      dialogSx: {
        maxWidth: 400,
      },
    });
  };
  const [{ loading }, handleCheckout] = useAsyncFn(async () => {
    const hasBlockedItems = checkBlockedItems(order?.line_items);
    if (hasBlockedItems || !order?.number) {
      showModal();
      return;
    }
    const res = await dispatch(checkoutRegistration.initiate(order.number));
    if ('error' in res) {
      return false;
    }

    const reCheck = checkBlockedItems(res.data?.line_items);
    if (reCheck) {
      showModal();
      await dispatch(getOrderByOrderNumber.initiate(order.number));
      return;
    }
    await dispatch(trackOfflineCheckout());
    await dispatch(getAddressesByUserId.initiate(customerId as number));

    if (billingAddress || shippingAddress) {
      await dispatch(
        changeAddressByOrderNumber.initiate({
          number: order.number,
          bill_address_attributes: billingAddress || shippingAddress,
          ship_address_attributes: shippingAddress || billingAddress,
        })
      );
    }

    await router.push(posRoutes.checkout);
    await dispatch(
      enterApp({
        page: 'checkout',
      })
    );
  }, [checkBlockedItems, order?.number, dispatch, router, showModal, customerId, billingAddress, shippingAddress]);

  const buttonLoading = orderLoading || loading || couponProcessing;

  return (
    <>
      <Button fullWidth onClick={handleCheckout} loading={buttonLoading} disabled={checkoutBtnStatus.disabled}>
        Checkout
      </Button>
      {modalContextHolder}
    </>
  );
};

export default PosCheckoutButton;
